import {inject} from '@angular/core';
import {map} from 'rxjs';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {hasAnyRoleInSet, loadUserRoles} from './shared/auth/role-access';
import {Routes} from '@angular/router';
import {AdminLayoutComponent} from './layout/admin-layout/admin-layout.component';
import {Callback} from './callback/callback';
import {Province} from './views/master/province/province';
import {CountryComponent} from './views/master/country/country';
import {UnitComponent} from './views/master/unit/unit';
import {BusinessTypeComponent} from './views/master/business-type/business-type';
import {DepartmentComponent} from './views/master/department/department';
import {DistrictComponent} from './views/master/district/district';
import {ProductGroupComponent} from './views/master/product-group/product-group';
import {ProductGroupTypeComponent} from './views/master/product-group-type/product-group-type';
import {StandardCodeComponent} from './views/master/standard-code/standard-code';
import {StandardTypeComponent} from './views/master/standard-type/standard-type';
import {SubdistrictComponent} from './views/master/subdistrict/subdistrict';
import {PostcodeComponent} from './views/master/postcode/postcode';
import {BanComponent} from './views/master/ban/ban';
import {ProductCodeComponent} from './views/master/product-code/product-code';
import {ProductCodeSubComponent} from './views/master/product-code-sub/product-code-sub';
import {CertificateAuthorityComponent} from './views/master/certificate-authority/certificate-authority';
import {WebServiceApiComponent} from './views/master/web-service-api/web-service-api';
import {WebServiceComponent} from './views/master/web-service/web-service';
import {SubCbComponent} from './views/master/sub-cb/sub-cb';
import {ActivityLogListComponent} from './views/settings/activity-log-list/activity-log-list.component';
import {LineSettingComponent} from './views/settings/line-setting/line-setting';
import {LogRetentionSettingComponent} from './views/settings/log-retention-setting/log-retention-setting';
import {WsLogListComponent} from './views/reports/ws-log/ws-log-list.component';
import {ThaitracesReqLogListComponent} from './views/reports/thaitraces-req-log/list/thaitraces-req-log-list.component';
import {TasLogListComponent} from './views/reports/tas-log/tas-log-list.component';
import {SignLogListComponent} from './views/reports/sign-log/list/sign-log-list.component';
import {
  ActivityLogReportListComponent
} from './views/reports/activity-log-report/list/activity-log-report-list.component';
import {
  SystemUsageSummaryListComponent
} from './views/reports/system-usage-summary/list/system-usage-summary-list.component';
import {AutoLoginPartialRoutesGuard} from 'angular-auth-oidc-client';
import {MENU_ROLES} from './shared/auth/role-access';
import {roleGuard} from './shared/auth/role.guard';
import {NotFound} from './views/not-found/not-found';
import {PageErrorComponent} from './shared/commons/page-error/page-error.component';
import {ChatHistoryComponent} from './views/campaigns/chat-history/chat-history.component';
import {ArticleListComponent} from './views/campaigns/articles/article-list.component';
import {PublicCoverLayoutComponent} from './layout/public-cover-layout/public-cover-layout.component';
import {PrPublicLayoutComponent} from './layout/pr-public-layout/pr-public-layout.component';
import {PrPublicLandingComponent} from './views/public/pr/pr-public-landing/pr-public-landing';
import {PrPublicShowComponent} from './views/public/pr/pr-public-show/pr-public-show';
import {FloatingChatComponent} from './views/floating-chat/floating-chat.component';

export const defaultLandingRedirect = () => {
  const oidcSecurityService = inject(OidcSecurityService);
  return loadUserRoles(oidcSecurityService).pipe(
    map(roles => hasAnyRoleInSet(roles, MENU_ROLES.certificateRequest)
      ? '/certificate-request/request-wait-check'
      : '/public/search/search-q'
    ),
  );
};

export const routes: Routes = [
  // {
  //   path: 'qmobile',
  //   component: QCertificateMobileComponent,
  // },
  {
    path: 'floating-chat-embed',
    component: FloatingChatComponent,
  },
  {
    path: 'callback',
    component: Callback,
  },
  {
    path: 'error/401',
    data: {title: 'เซสชันหมดอายุหรือไม่ได้รับอนุญาต'},
    component: PageErrorComponent,
  },
  {
    path: 'logout',
    data: {title: 'ออกจากระบบแล้ว'},
    component: PageErrorComponent,
  },
  {
    path: 'error/403',
    data: {title: 'ไม่มีสิทธิ์เข้าถึง'},
    component: PageErrorComponent,
  },
  {
    path: 'error/:code',
    component: PageErrorComponent,
  },
  {
    path: 'unauthorized',
    redirectTo: 'error/401',
    pathMatch: 'full',
  },
  {
    path: 'pr',
    component: PrPublicLayoutComponent,
    children: [
      {path: '', component: PrPublicLandingComponent, data: {title: 'เพจประชาสัมพันธ์'}},
      {path: ':runpage', component: PrPublicShowComponent, data: {title: 'เพจประชาสัมพันธ์'}},
    ]
  },
  {
    path: 'pr-headless',
    children: [
      {path: '', component: PrPublicLandingComponent, data: {title: 'เพจประชาสัมพันธ์', headless: true}},
      // "ดูทั้งหมด" inside the embed; must stay above :runpage.
      {
        path: 'list',
        loadComponent: () => import('./views/public/pr/pr-public-list/pr-public-list').then(m => m.PrPublicListComponent),
        data: {title: 'รายการเพจประชาสัมพันธ์', headless: true}
      },
      {path: ':runpage', component: PrPublicShowComponent, data: {title: 'เพจประชาสัมพันธ์', headless: true}},
    ]
  },
  {
    path: 'public',
    component: PublicCoverLayoutComponent,
    loadChildren: () => import('./views/public/public.module').then(m => m.PublicModule)
  },
  {
    path: '',
    data: {title: 'หน้าแรก'},
    component: AdminLayoutComponent,
    canActivate: [AutoLoginPartialRoutesGuard],
    children: [
      {
        path: '',
        redirectTo: defaultLandingRedirect,
        pathMatch: 'full'
      },
      {
        path: '',
        data: {title: 'ข้อมูลพื้นฐาน'},
        children: [
          {
            path: 'provinces',
            data: {title: 'ข้อมูลจังหวัด', roles: MENU_ROLES.provinces},
            canActivate: [roleGuard],
            component: Province
          },
          {
            path: 'country',
            data: {title: 'รายชื่อประเทศตาม ISO 3166', roles: MENU_ROLES.country},
            canActivate: [roleGuard],
            component: CountryComponent
          },
          {
            path: 'units',
            data: {title: 'ข้อมูลหน่วยนับ', roles: MENU_ROLES.units},
            canActivate: [roleGuard],
            component: UnitComponent
          },
          {
            path: 'business-types',
            data: {title: 'ข้อมูลประเภทธุรกิจ', roles: MENU_ROLES.businessTypes},
            canActivate: [roleGuard],
            component: BusinessTypeComponent
          },
          {
            path: 'departments',
            data: {title: 'ข้อมูลผู้ประกอบการตรวจสอบมาตรฐาน', roles: MENU_ROLES.departments},
            canActivate: [roleGuard],
            component: DepartmentComponent
          },
          {
            path: 'sub-cbs',
            data: {title: 'ข้อมูลหน่วยตรวจสอบย่อย', roles: MENU_ROLES.subCbs},
            canActivate: [roleGuard],
            component: SubCbComponent
          },
          {
            path: 'districts',
            data: {title: 'ข้อมูลอำเภอ', roles: MENU_ROLES.districts},
            canActivate: [roleGuard],
            component: DistrictComponent
          },
          {
            path: 'subdistricts',
            data: {title: 'ข้อมูลตำบล/แขวง', roles: MENU_ROLES.subdistricts},
            canActivate: [roleGuard],
            component: SubdistrictComponent
          },
          {
            path: 'postcodes',
            data: {title: 'ข้อมูลรหัสไปรษณีย์', roles: MENU_ROLES.postcodes},
            canActivate: [roleGuard],
            component: PostcodeComponent
          },
          {
            path: 'product-group-types',
            data: {title: 'ข้อมูลลักษณะกลุ่มสินค้า', roles: MENU_ROLES.productGroupTypes},
            canActivate: [roleGuard],
            component: ProductGroupTypeComponent
          },
          {
            path: 'product-groups',
            data: {title: 'ข้อมูลกลุ่มสินค้า', roles: MENU_ROLES.productGroups},
            canActivate: [roleGuard],
            component: ProductGroupComponent
          },
          {
            path: 'product-codes',
            data: {title: 'ข้อมูลรหัสชนิดสินค้า', roles: MENU_ROLES.productCodes},
            canActivate: [roleGuard],
            component: ProductCodeComponent
          },
          {
            path: 'product-code-subs',
            data: {title: 'ประเภทสินค้า (ย่อย)', roles: MENU_ROLES.productCodeSubs},
            canActivate: [roleGuard],
            component: ProductCodeSubComponent
          },
          {
            path: 'standard-types',
            data: {title: 'หมวดรหัสมาตรฐานสินค้า', roles: MENU_ROLES.standardTypes},
            canActivate: [roleGuard],
            component: StandardTypeComponent
          },
          {
            path: 'standard-codes',
            data: {title: 'รหัสมาตรฐานสินค้า', roles: MENU_ROLES.standardCodes},
            canActivate: [roleGuard],
            component: StandardCodeComponent
          },
          {
            path: 'setting-bans',
            data: {title: 'ระบบจัดการคำต้องห้าม', roles: MENU_ROLES.settingBans},
            canActivate: [roleGuard],
            component: BanComponent
          },
          {
            path: 'certificate-authority',
            data: {
              title: 'ผู้ให้บริการออกใบรับรองอิเล็กทรอนิกส์ (CA)',
              roles: MENU_ROLES.certificateAuthority
            },
            canActivate: [roleGuard],
            component: CertificateAuthorityComponent
          },
          {
            path: 'ws-settings',
            data: {title: 'ตั้งค่าข้อมูล API', roles: MENU_ROLES.wsSettings},
            canActivate: [roleGuard],
            component: WebServiceApiComponent
          },
          {
            path: 'ws-settings/:id',
            data: {title: 'รายละเอียด API', roles: MENU_ROLES.wsSettings},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/master/web-service-api/web-service-api-detail').then(m => m.WebServiceApiDetailComponent)
          },
          {
            path: 'wss',
            data: {title: 'ผู้ใช้งาน Web Service API', roles: MENU_ROLES.wss},
            canActivate: [roleGuard],
            component: WebServiceComponent
          },
          {
            path: 'wss/:id',
            data: {title: 'รายละเอียดผู้ใช้งาน Web Service API', roles: MENU_ROLES.wss},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/master/web-service/web-service-detail').then(m => m.WebServiceDetailComponent)
          },
          {
            path: 'activity-logs',
            data: {title: 'ตั้งค่าสถานะ Log', roles: MENU_ROLES.activityLogs},
            canActivate: [roleGuard],
            component: ActivityLogListComponent
          },
          {
            path: 'line',
            data: {title: 'ตั้งค่า LINE', roles: MENU_ROLES.lineSettings},
            canActivate: [roleGuard],
            component: LineSettingComponent
          },
          {
            path: 'log-retention',
            data: {title: 'จำนวนวันเก็บ Log', roles: MENU_ROLES.logRetention},
            canActivate: [roleGuard],
            component: LogRetentionSettingComponent
          },
          {
            path: 'tas-license-mokoso2',
            data: {title: 'API Tas-License สำหรับผู้ผลิต', roles: MENU_ROLES.tasLicenseMokoso2},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/tas-license-mokoso2-list/tas-license-mokoso2-list.component').then(m => m.TasLicenseMokoso2ListComponent)
          },
          {
            path: 'cab-permit-approved',
            data: {title: 'API CAB Service', roles: MENU_ROLES.cabPermitApproved},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/cab-permit-list/cab-permit-list.component').then(m => m.CabPermitListComponent)
          },
          {
            path: 'tas2go-standard',
            data: {title: 'API Tas2Go', roles: MENU_ROLES.tas2goStandard},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/tas2go-standard-list/tas2go-standard-list.component').then(m => m.Tas2GoStandardListComponent)
          },
          {
            path: 'gdx-juristic',
            data: {title: 'GDX ค้นหาข้อมูลนิติบุคคล', roles: MENU_ROLES.gdxJuristic},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/gdx-juristic-search/gdx-juristic-search.component').then(m => m.GdxJuristicSearchComponent)
          },
          {
            path: 'gdx-cooperative',
            data: {title: 'GDX ค้นหาข้อมูลจดทะเบียนสหกรณ์', roles: MENU_ROLES.gdxCooperative},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/gdx-cooperative-search/gdx-cooperative-search.component').then(m => m.GdxCooperativeSearchComponent)
          },
          {
            path: 'gdx-smce',
            data: {title: 'ค้นหาข้อมูลวิสาหกิจชุมชน', roles: MENU_ROLES.gdxSmce},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/gdx-smce-search/gdx-smce-search.component').then(m => m.GdxSmceSearchComponent)
          },
          {
            path: 'smce-scrape',
            data: {title: 'GDX ดึงข้อมูลวิสาหกิจชุมชน', roles: MENU_ROLES.smceScrape},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/smce-scrape/smce-scrape.component').then(m => m.SmceScrapeComponent)
          },
          {
            path: 'standard-logo-sync',
            data: {title: 'เครื่องหมายมาตรฐาน (ThaiTraces)'},
            loadComponent: () => import('./views/settings/standard-logo-sync/standard-logo-sync.component').then(m => m.StandardLogoSyncComponent)
          },
          {
            path: 'user-lk2-token',
            data: {title: 'ข้อมูล Token LK2', roles: MENU_ROLES.userLk2Token},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/settings/user-lk2-token-list/user-lk2-token-list.component').then(m => m.UserLk2TokenListComponent)
          },
        ]
      },
      {
        path: 'reports',
        data: {title: 'รายงาน', roles: MENU_ROLES.reports},
        canActivate: [roleGuard],
        children: [
          {
            path: 'tas-logs',
            data: {title: 'Log การเรียกข้อมูลจากระบบ TAS', roles: MENU_ROLES.tasLogs},
            canActivate: [roleGuard],
            component: TasLogListComponent
          },
          {
            path: 'thaitraces-req-log',
            data: {title: 'Log การเรียกข้อมูลจากระบบ ThaiTraces', roles: MENU_ROLES.thaiTracesReqLog},
            canActivate: [roleGuard],
            component: ThaitracesReqLogListComponent
          },
          {
            path: 'ws-logs',
            data: {title: 'Log การเชื่อมโยงข้อมูล ThaiTraces', roles: MENU_ROLES.wsLogs},
            canActivate: [roleGuard],
            component: WsLogListComponent
          },
          {
            path: 'thaitraces-delivery-queue',
            data: {title: 'คิวส่งสถานะ ThaiTraces', roles: MENU_ROLES.thaiTracesDeliveryQueue},
            canActivate: [roleGuard],
            loadComponent: () => import('./views/reports/thaitraces-delivery-queue/thaitraces-delivery-queue.component')
              .then(m => m.ThaiTracesDeliveryQueueComponent)
          },
          {
            path: 'sign-log',
            data: {title: 'Log การลงลายมือชื่อ', roles: MENU_ROLES.signLog},
            canActivate: [roleGuard],
            component: SignLogListComponent
          },
          {
            path: 'activity-log-report',
            data: {title: 'รายการบันทึกกิจกรรม (Log)', roles: MENU_ROLES.activityLogReport},
            canActivate: [roleGuard],
            component: ActivityLogReportListComponent
          },
          {
            path: 'system-usage-summary',
            data: {title: 'รายงานสรุปการใช้งานภายในระบบ', roles: MENU_ROLES.systemUsageSummary},
            canActivate: [roleGuard],
            component: SystemUsageSummaryListComponent
          },
        ]
      },
      {
        path: 'certificate-request',
        data: {title: 'การขอรหัสการรับรองฯ', roles: MENU_ROLES.certificateRequest},
        canActivate: [roleGuard],
        loadChildren: () => import('./views/certificate-request/certificate-request.module').then(m => m.CertificateRequestModule)
      },
      {
        path: 'import-request',
        data: {title: 'การกำหนดรหัสสำหรับผู้นำเข้าฯ', roles: MENU_ROLES.importerCode},
        canActivate: [roleGuard],
        loadChildren: () => import('./views/import-request/import-request.module').then(m => m.ImportRequestModule)
      },
      {
        path: 'campaigns',
        data: {title: 'แชท/บทความ', roles: MENU_ROLES.campaigns},
        canActivate: [roleGuard],
        children: [
          {path: '', redirectTo: 'chat-history', pathMatch: 'full'},
          {path: 'chat-history', data: {title: 'ประวัติแชท'}, component: ChatHistoryComponent},
          {
            path: 'chat-history/:id',
            data: {title: 'ประวัติการสนทนา'},
            loadComponent: () => import('./views/campaigns/chat-history/chat-history-detail.component')
              .then(m => m.ChatHistoryDetailComponent)
          },
          {
            path: 'articles/new',
            data: {title: 'สร้างบทความ'},
            loadComponent: () => import('./views/public/article/article-editor').then(m => m.ArticleEditorComponent)
          },
          {
            path: 'articles/:id/edit',
            data: {title: 'แก้ไขบทความ'},
            loadComponent: () => import('./views/public/article/article-editor').then(m => m.ArticleEditorComponent)
          },
          {path: 'articles', data: {title: 'บทความ'}, component: ArticleListComponent},
        ]
      },
      {
        path: 'survey',
        data: {title: 'แบบสอบถาม', roles: MENU_ROLES.survey},
        canActivate: [roleGuard],
        loadComponent: () => import('./views/public/survey/survey.component').then(m => m.SurveyComponent)
      },
      {path: '**', component: NotFound},
    ]
  }
];
